import { SealsDto, SealType } from '@planning/app/api'
import {
  extractSubFormData,
  FieldStore,
  ISubFormStore,
  ListFieldStore,
  required,
  validateSubForm,
} from '@tom-ui/utils'
import { action, makeObservable, observable, runInAction } from 'mobx'

export type OrderSealsSubFormType = {
  id?: number
  hasSeals: boolean
  seals?: SealsDto[]
}

export type OrderSealType = {
  id?: number | null
  number: FieldStore<string>
  type: FieldStore<SealType | ''>
  isNewSeal: FieldStore<boolean>
  isMissingSeal: FieldStore<boolean>
}

const createSealsItem = (value?: SealsDto): OrderSealType => ({
  id: value?.id,
  number: new FieldStore(value?.number ?? '', required()),
  type: new FieldStore(value?.type ?? ''),
  isNewSeal: new FieldStore(value?.isNewSeal ?? false),
  isMissingSeal: new FieldStore(value?.isMissingSeal ?? false),
})

export class OrderSealsSubFormStore implements ISubFormStore<OrderSealsSubFormType> {
  hasSeals: FieldStore<boolean> = new FieldStore<boolean>(false)
  seals = new ListFieldStore<OrderSealType>([], (val: OrderSealType[]) => {
    const sealNumbers = new Set<string>()

    val.filter(item => item.number.isTouched)
    for (let index = 0; index < val.length; index++) {
      const reference = val[index].number.value

      if (sealNumbers.has(reference)) {
        return `duplicateValue ${reference}`
      } else {
        sealNumbers.add(reference)
      }
    }
  })

  constructor(initialValues?: Partial<OrderSealsSubFormType>) {
    makeObservable(this, {
      seals: observable,
      addSeal: action,
      removeSeal: action,
    })

    this.hasSeals = new FieldStore<boolean>(initialValues?.hasSeals ?? false)
    runInAction(() => {
      initialValues?.seals?.forEach(seal => {
        this.seals.push(createSealsItem(seal))
      })
    })
  }

  setValues(values?: Partial<OrderSealsSubFormType>) {
    runInAction(() => {
      this.hasSeals.setValue(values?.hasSeals ?? false)
      this.seals.setValue(values?.seals?.map(s => createSealsItem(s)) ?? [])
    })
  }

  addSeal = (value?: SealsDto) => {
    this.seals.push(createSealsItem(value))
  }

  removeSeal = (index: number) => {
    this.seals.removeByIndex(index)
  }

  validate = () => {
    if (!this.hasSeals.value) {
      return true
    }
    return validateSubForm(this)
  }

  get data(): OrderSealsSubFormType {
    if (!this.hasSeals.value) {
      return {
        hasSeals: this.hasSeals.value,
      }
    }

    return {
      hasSeals: this.hasSeals.value,
      seals: extractSubFormData(this, true).seals,
    }
  }
}
